import { forwardRef } from 'react';

import {
  CloseDrawerButton,
  Divider,
  Drawer,
  DrawerProps,
  useDrawer,
  useDrawerInstance,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { Node } from '@cast/types';

import { DrawerContent } from 'components/drawer/ds';
import { drainFailedAnnotation } from 'constants/cluster';

const failedToDrainView = (
  <Stack>
    <Typography variant="P14R">
      Node failed to drain in time during rebalancing
      <br />
      Annotation was applied:
    </Typography>
    <Typography
      variant="C14"
      color="grey.600"
      borderRadius="2px"
      border={tokens.theme.border.structural.neutral}
      bgcolor="grey.100"
      px={2}
      mb={24}
    >
      {drainFailedAnnotation}
    </Typography>
    <Typography color="textSecondary" mb={8}>
      Recommendation:
    </Typography>
    <Box component="ul" sx={{ pl: 18, my: 0, '& li': { typography: 'P14R' } }}>
      <li>
        Make sure there are no Pod Disruption Budgets that prevent the pods from
        moving to other nodes.
      </li>
      <li>Remove the annotation</li>
      <li>Uncordon the node</li>
    </Box>
  </Stack>
);

const NodeIssuesDrawer = forwardRef<HTMLDivElement, DrawerProps>(
  (props: DrawerProps, ref) => {
    return (
      <Drawer size="small" {...props} ref={ref} testId="node-issues-drawer">
        <CloseDrawerButton />
        <DrawerContent mt={68}>
          <Typography variant="h5" mb={16}>
            Issues:
          </Typography>
          <Divider sx={{ mb: 24 }} />
          <Stack>
            <Typography variant="L12R" color="textSecondary" mb={8}>
              ISSUE:
            </Typography>
            {failedToDrainView}
          </Stack>
        </DrawerContent>
      </Drawer>
    );
  }
);

NodeIssuesDrawer.displayName = 'NodeIssuesDrawer';

export const useNodeIssuesDrawer = () => {
  const { id } = useDrawerInstance();
  const { open } = useDrawer();
  return {
    openNodeIssuesDrawer: (node: Node) => {
      if (!node.isFailedToDrain) {
        return;
      }
      open({
        component: NodeIssuesDrawer,
        props: {},
        options: { parentId: id },
      });
    },
  };
};
